import { useState, useEffect, useCallback } from 'react';
import { getNotifications, markNotificationRead, markAllNotificationsRead } from '../api/notifications';
import { Notification, PaginatedResponse } from '../types/index';

interface UseNotificationsReturn {
  notifications: Notification[];
  unreadCount: number;
  isLoading: boolean;
  markRead: (id: string) => Promise<void>;
  markAllRead: () => Promise<void>;
  refetch: () => Promise<void>;
}

export const useNotifications = (): UseNotificationsReturn => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const unreadCount = notifications.filter((n) => !n.isRead).length;

  const refetch = useCallback(async () => {
    setIsLoading(true);

    try {
      const data: PaginatedResponse<Notification> = await getNotifications({
        page: 1,
        pageSize: 50,
      });
      setNotifications(data.items);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const markRead = useCallback(async (id: string) => {
    try {
      const updated = await markNotificationRead(id);
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? updated : n))
      );
    } catch (error) {
      // Bug #322: this catch used to hide a 404 from a wrong URL, so a failed
      // write looked identical to a successful one. The URLs are fixed, but the
      // swallow stays deliberately quiet (the bell fires this without awaiting)
      // — the reconciling refetch below is what stops a failed write from
      // leaving the UI showing state the server never accepted.
      console.error('Failed to mark notification as read:', error);
      void refetch();
    }
  }, [refetch]);

  const markAllRead = useCallback(async () => {
    try {
      await markAllNotificationsRead();
      // Optimistic update so the badge clears immediately...
      setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
      // ...then reconcile against the server rather than trusting it.
      void refetch();
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error);
      void refetch();
    }
  }, [refetch]);

  useEffect(() => {
    refetch();

    // Poll for new notifications every 60 seconds
    const interval = setInterval(refetch, 60000);

    return () => clearInterval(interval);
  }, [refetch]);

  return {
    notifications,
    unreadCount,
    isLoading,
    markRead,
    markAllRead,
    refetch,
  };
};
